import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useDispatch } from 'react-redux';
import {
  setScreenType,
  useCallId,
  useCallNumber,
  useCallType,
  useScreenType
} from '../../../store/slices/dialSlice';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import {
  attendedTransfer,
  blindTransfer,
  transferUserOptionGet,
  transferProcessOptionGet,
  transferQueueOptionGet,
  transferIvrOptionGet
} from '../../../store/slices/callingSlice';
import { useUserDetails } from '../../../store/slices/authSlice';
import FormSelect from '../../form/FormSelect';
import { convertToMask } from '../../../utils/common';
import { useTranslation } from 'react-i18next';

const TRANSFER_TYPES = [
  { key: 'agent', label: 'Agent' },
  { key: 'process', label: 'Process' },
  { key: 'queue', label: 'Queue' },
  { key: 'ivr', label: 'IVR' }
];

const UserTransferScreen = ({ number }) => {
  const callNumber = useCallNumber();
  const user = useUserDetails();
  const callId = useCallId();
  const callType = useCallType();
  const screenType = useScreenType();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [transferType, setTransferType] = useState('agent');
  const [targetOptions, setTargetOptions] = useState([]);
  const [targetId, setTargetId] = useState('');

  const fetchTargetOptions = async (type) => {
    try {
      let options = [];
      if (type === 'process') {
        const response = await dispatch(transferProcessOptionGet()).unwrap();
        options = (response?.data || []).map((item) => ({
          label: item.name,
          value: item.id
        }));
      } else if (type === 'queue') {
        const response = await dispatch(transferQueueOptionGet()).unwrap();
        options = (response?.data || []).map((item) => ({
          label: item.name,
          value: item.id
        }));
      } else if (type === 'ivr') {
        const response = await dispatch(transferIvrOptionGet()).unwrap();
        options = (response?.data || []).map((item) => ({
          label: item.name,
          value: item.id
        }));
      } else {
        const response = await dispatch(transferUserOptionGet()).unwrap();
        if (response && response.success) {
          options = (response?.data || []).map((item) => ({
            // Username with the agent's name in brackets; react-select searches
            // on this label, so typing the agent name filters the list.
            label: item.agent_name ? `${item.Username} (${item.agent_name})` : item.Username,
            value: item.id
          }));
        }
      }
      setTargetOptions(options || []);
    } catch (error) {
      setTargetOptions([]);
      console.error('Error fetching transfer target options:', error);
    }
  };

  // Reload the target list whenever the transfer type changes.
  useEffect(() => {
    setTargetId('');
    fetchTargetOptions(transferType);
  }, [transferType]);

  const doTransfer = async () => {
    try {
      const payload = {
        uuid: callId,
        calltype: callType,
        transfer_type: transferType,
        target_id: targetId,
        // Backward-compat: the API falls back to selected_user_id for agent transfers.
        selected_user_id: targetId
      };
      if (screenType === 'BLIND_TRANSFER') {
        await dispatch(blindTransfer(payload)).unwrap();
      } else {
        await dispatch(attendedTransfer(payload)).unwrap();
        dispatch(setScreenType('TRANSFER_HANGUP'));
      }
    } catch (error) {
      console.log('transfer error', error);
    }
  };

  useEffect(() => {
    if (targetId) {
      doTransfer();
    }
  }, [targetId]);

  const placeholder =
    transferType === 'process'
      ? 'Select Process'
      : transferType === 'queue'
      ? 'Select Queue'
      : transferType === 'ivr'
      ? 'Select IVR'
      : 'Select User';

  return (
    <div className="h-100 call-action-btn-container">
      <div className="mt-auto mb-3 text-white text-center fw-bold fs-4">
        {user?.process_details?.numberMasking === 'true'
          ? convertToMask(callNumber || number)
          : callNumber || number}
      </div>

      <div className="d-flex justify-content-center gap-2 mb-3">
        {TRANSFER_TYPES.map((tt) => (
          <Button
            key={tt.key}
            size="sm"
            color={transferType === tt.key ? 'primary' : 'dark'}
            onClick={() => setTransferType(tt.key)}>
            {t(tt.key) !== tt.key ? t(tt.key) : tt.label}
          </Button>
        ))}
      </div>

      <div className="text-white mb-auto">
        <FormSelect
          isShowLabel={false}
          name="targetId"
          label={t('user')}
          placeholder={placeholder}
          value={targetId}
          options={targetOptions}
          onChange={(e) => {
            setTargetId(e?.target?.value);
          }}
        />
      </div>
      <div className="call-button-wrapper mt-auto pb-4">
        <Button
          className="back-button"
          onClick={() => {
            dispatch(setScreenType('TRANSFER'));
          }}>
          <Icon.ArrowLeft className="back-icon text-primary" />
        </Button>
      </div>
    </div>
  );
};

UserTransferScreen.propTypes = {
  number: PropTypes.string
};

export default UserTransferScreen;
